/** UXP-00002 — Executive Home Workspace framework (mirrors pkg/exechome) */

export const UXP_EH_ID = 'UXP-00002';

export interface HealthScore {
  dimension: string;
  label: string;
  score: number;
  trend?: string;
}

export interface QuickActionSpec {
  action: string;
  label: string;
  path?: string;
}

export interface TaskItem {
  id: string;
  title: string;
  category: string;
  due?: string;
  priority?: string;
}

export interface TimelineEvent {
  id: string;
  type: string;
  title: string;
  timestamp: string;
}

export const WORKSPACE_REGIONS = [
  'Welcome', 'Daily Briefing', 'Business Health', 'Executive KPIs', 'AI Opportunities',
  'AI Risks', 'Decision Center', 'Task Center', 'Team Collaboration', 'AI Conversation Hub',
  'Timeline', 'Executive Calendar', 'Marketplace Overview', 'Brand Overview', 'Smart Search',
  'Quick Actions', 'Insights Hub', 'Notification Center', 'Workspace Health',
];

export const BRIEFING_TYPES = [
  'Morning Brief', 'Afternoon Review', 'Evening Summary', 'Weekly Executive Brief', 'Monthly Executive Brief',
];

export const HEALTH_DIMENSIONS = [
  'Overall Business', 'Revenue', 'Profit', 'Advertising', 'Inventory', 'Brand',
  'Customer', 'Content', 'Finance', 'Marketplace', 'Operational', 'AI Confidence',
];

export const DEFAULT_HEALTH_SCORES: HealthScore[] = [
  { dimension: 'overall_business', label: 'Overall Business', score: 82, trend: '+3' },
  { dimension: 'revenue', label: 'Revenue', score: 88, trend: '+5' },
  { dimension: 'profit', label: 'Profit', score: 76, trend: '-2' },
  { dimension: 'advertising', label: 'Advertising', score: 91, trend: '+8' },
  { dimension: 'inventory', label: 'Inventory', score: 68, trend: '-5' },
  { dimension: 'brand', label: 'Brand', score: 85, trend: 'stable' },
  { dimension: 'customer', label: 'Customer', score: 79, trend: '+1' },
  { dimension: 'content', label: 'Content', score: 74, trend: '+4' },
  { dimension: 'finance', label: 'Finance', score: 81, trend: 'stable' },
  { dimension: 'marketplace', label: 'Marketplace', score: 93, trend: '+2' },
  { dimension: 'operational', label: 'Operational', score: 77, trend: '-1' },
  { dimension: 'ai_confidence', label: 'AI Confidence', score: 86, trend: '+6' },
];

export const EXECUTIVE_KPIS = [
  { id: 'revenue', label: 'Revenue', value: '$124.5K', change: '+12%' },
  { id: 'orders', label: 'Orders', value: '1,847', change: '+8%' },
  { id: 'profit', label: 'Profit', value: '$28.3K', change: '+5%' },
  { id: 'roas', label: 'ROAS', value: '5.2x', change: '+0.4' },
  { id: 'acos', label: 'ACOS', value: '19.2%', change: '-1.1%' },
  { id: 'tacos', label: 'TACOS', value: '11.8%', change: '-0.6%' },
  { id: 'traffic', label: 'Traffic', value: '42.1K', change: '+15%' },
  { id: 'sessions', label: 'Sessions', value: '38.7K', change: '+11%' },
  { id: 'conversion', label: 'Conversion', value: '4.8%', change: '+0.3%' },
  { id: 'inventory_value', label: 'Inventory Value', value: '$892K', change: 'stable' },
  { id: 'cash_flow', label: 'Cash Flow', value: '$45.2K', change: '+7%' },
  { id: 'settlement_status', label: 'Settlement', value: 'Current', change: 'ok' },
  { id: 'review_rating', label: 'Review Rating', value: '4.6★', change: '+0.1' },
  { id: 'suppressed_listings', label: 'Suppressed', value: '2', change: '-1' },
];

export const OPPORTUNITY_TYPES = [
  'Revenue', 'Profit', 'Catalog', 'Advertising', 'Inventory', 'Customer', 'Brand', 'Operational',
];

export const RISK_TYPES = [
  'Inventory', 'Policy Violations', 'Listing Suppression', 'Advertising Waste',
  'Fee Leakage', 'Negative Reviews', 'Security Events', 'Marketplace Issues',
];

export const DEMO_OPPORTUNITIES = [
  'Weekend ROAS 7.2 vs weekday 5.1 — dayparting opportunity (+$4.2K/mo)',
  '5 listings eligible for AI SEO optimization (+8% organic sessions)',
  'Cross-sell bundle opportunity on top 3 ASINs',
  'Brand analytics shows rising search volume in category',
];

export const DEMO_RISKS = [
  'Stockout risk — EARBUDS-BLK-001 (12 days cover remaining)',
  '3 listings below SEO threshold — suppression risk',
  'Settlement mismatch — $127.40 unreconciled',
  'SP campaign ACOS 42% on 2 ad groups — waste detected',
];

export const QUICK_ACTIONS: QuickActionSpec[] = [
  { action: 'generate_hero_images', label: 'Generate Hero Images', path: '/ai/creative' },
  { action: 'generate_a_plus', label: 'Generate A+', path: '/ai/creative' },
  { action: 'optimize_listings', label: 'Optimize Listings', path: '/ai/listing' },
  { action: 'launch_campaign', label: 'Launch Campaign', path: '/amazon/revenue-ops' },
  { action: 'review_inventory', label: 'Review Inventory', path: '/amazon/inventory-intel' },
  { action: 'settlement_review', label: 'Settlement Review', path: '/amazon/finance-ops' },
  { action: 'invite_user', label: 'Invite User', path: '/admin' },
  { action: 'connect_marketplace', label: 'Connect Marketplace', path: '/amazon/connect' },
];

export const DEMO_TASKS: TaskItem[] = [
  { id: 't1', title: 'Approve SP budget increase +20%', category: 'assigned', due: 'Today', priority: 'high' },
  { id: 't2', title: 'Review FBA reimbursement claim', category: 'overdue', due: 'Yesterday', priority: 'critical' },
  { id: 't3', title: 'Optimize 3 low-CTR listings', category: 'ai_suggested', due: 'This week', priority: 'normal' },
  { id: 't4', title: 'Weekly inventory reconciliation', category: 'recurring', due: 'Friday', priority: 'normal' },
  { id: 't5', title: 'Schedule summer release package', category: 'scheduled', due: 'Mar 15', priority: 'normal' },
];

export const DEMO_TIMELINE: TimelineEvent[] = [
  { id: 'tl-1', type: 'synchronization', title: 'Catalog sync completed — 847 products', timestamp: '30m ago' },
  { id: 'tl-2', type: 'ai_decision', title: 'AI recommended budget reallocation', timestamp: '2h ago' },
  { id: 'tl-3', type: 'publishing', title: 'Release REL-2026-000145 scheduled', timestamp: '4h ago' },
  { id: 'tl-4', type: 'marketplace_event', title: 'Amazon settlement posted', timestamp: '6h ago' },
  { id: 'tl-5', type: 'audit_event', title: 'User approved listing update', timestamp: '8h ago' },
];

export const BRIEFING_SECTIONS = [
  { title: 'Marketplace Insights', priority: 'high', summary: '3 listings show CTR decline; 2 inventory SKUs at risk' },
  { title: 'Catalog & SEO Suggestions', priority: 'normal', summary: '5 listings eligible for AI optimization' },
  { title: 'Inventory Risks', priority: 'critical', summary: 'Out-of-stock risk elevated for top SKUs' },
  { title: 'Operational Alerts', priority: 'high', summary: '1 workflow execution failed overnight' },
];

export const DEMO_DECISIONS = [
  { id: 'dec-1', title: 'Approve SP budget increase +20%', category: 'high_impact', impact: '+$4,200/month sales', confidence: 0.91, requires_approval: true, source_module: 'revenueops' },
  { id: 'dec-2', title: 'File FBA reimbursement — 3 lost units', category: 'critical', impact: 'Recover $89.97', confidence: 0.94, requires_approval: true, source_module: 'financeops' },
  { id: 'dec-3', title: 'Refresh CASE-PRO listing title', category: 'optimization', impact: '+8% organic sessions', confidence: 0.82, requires_approval: false, source_module: 'contentintel' },
];

export const NOTIFICATION_CATEGORIES = [
  'Critical', 'Business', 'AI', 'Marketplace', 'Approvals', 'Finance', 'Advertising', 'System',
];

export const WORKSPACE_HEALTH_METRICS = [
  { label: 'Background Jobs', status: 'healthy', detail: '2 running' },
  { label: 'Synchronization', status: 'healthy', detail: 'Last sync 30m ago' },
  { label: 'API Health', status: 'healthy', detail: 'All endpoints OK' },
  { label: 'Queue Health', status: 'warning', detail: '142 items pending' },
  { label: 'Storage', status: 'healthy', detail: '68% used' },
  { label: 'Credits', status: 'healthy', detail: '8,420 remaining' },
  { label: 'AI Usage', status: 'healthy', detail: '124 requests today' },
];

export const ANALYTICS_METRICS = [
  'Dashboard Usage', 'AI Adoption', 'Recommendation Acceptance',
  'Decision Speed', 'Productivity', 'Time to Value',
];

export interface ExecHomeDashboard {
  workspace_regions: number;
  briefing_types: number;
  health_dimensions: number;
  executive_kpis: number;
  opportunity_types: number;
  risk_types: number;
  quick_actions: number;
  notification_categories: number;
}

export function buildExecHomeDashboard(): ExecHomeDashboard {
  return {
    workspace_regions: WORKSPACE_REGIONS.length,
    briefing_types: BRIEFING_TYPES.length,
    health_dimensions: HEALTH_DIMENSIONS.length,
    executive_kpis: EXECUTIVE_KPIS.length,
    opportunity_types: OPPORTUNITY_TYPES.length,
    risk_types: RISK_TYPES.length,
    quick_actions: QUICK_ACTIONS.length,
    notification_categories: NOTIFICATION_CATEGORIES.length,
  };
}

export const LIVE_ROUTES = {
  dashboard: '/dashboard',
  command: '/command',
  dailyOps: '/executive/daily-ops',
} as const;

export function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
